import React from 'react';
import { Phone, MapPin, Clock, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import * as LucideIcons from 'lucide-react';
import { companyInfo, services, testimonials } from '../mockData';

const Home = () => {
  const handleCallNow = () => {
    window.location.href = `tel:${companyInfo.mobile}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-400 to-yellow-500 shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{companyInfo.name}</h1>
              <p className="text-sm text-gray-800 mt-1">{companyInfo.address}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 bg-gray-900 text-yellow-400 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors">
                <Phone className="w-4 h-4" />
                <span className="font-semibold">{companyInfo.phone}</span>
              </a>
              <a href={`tel:${companyInfo.mobile}`} className="flex items-center gap-2 bg-gray-900 text-yellow-400 px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors">
                <Phone className="w-4 h-4" />
                <span className="font-semibold">{companyInfo.mobile}</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-[600px] bg-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1556122071-e404eaedb77f"
            alt="Taksi Hizmeti"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 to-gray-900/50"></div>
        </div>
        
        <div className="relative container mx-auto px-4 h-full flex items-center justify-center">
          <div className="text-center text-white max-w-3xl">
            <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="text-yellow-400">Gelibolu Hastane Taksi</span>
            </h2>
            <p className="text-xl md:text-2xl mb-8 text-gray-200">
              Güvenli ve Hızlı Taksi Hizmeti
            </p>
            <Button 
              onClick={handleCallNow}
              size="lg"
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold text-xl px-12 py-8 h-auto rounded-xl shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <Phone className="w-6 h-6 mr-3" />
              Hemen Taksi Çağır
            </Button>
            <div className="mt-6 flex items-center justify-center gap-2 text-yellow-400">
              <Clock className="w-5 h-5" />
              <span className="text-lg font-semibold">7/24 Kesintisiz Hizmet</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Hizmetlerimiz</h2>
            <p className="text-xl text-gray-600">Profesyonel ve güvenilir ulaşım çözümleri</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <Card key={service.id} className="border-2 hover:border-yellow-400 transition-all duration-300 hover:shadow-xl">
                  <CardHeader>
                    <div className="w-14 h-14 bg-yellow-400 rounded-xl flex items-center justify-center mb-4">
                      {IconComponent && <IconComponent className="w-7 h-7 text-gray-900" />}
                    </div>
                    <CardTitle className="text-2xl text-gray-900">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-gray-600">{service.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Hakkımızda</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1482029255085-35a4a48b7084"
                  alt="Profesyonel Sürücü"
                  className="rounded-2xl shadow-xl"
                />
              </div>
              <div className="space-y-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  Gelibolu Hastane Taksi Durağı olarak yılların deneyimi ile Gelibolu ve çevresinde 
                  güvenilir, konforlu ve profesyonel taksi hizmeti sunuyoruz.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Modern araç filomuz ve deneyimli sürücü kadromuzla 7/24 hizmetinizdeyiz. 
                  Hastane transferlerinden şehirler arası yolculuklara kadar her türlü ulaşım 
                  ihtiyacınız için yanınızdayız.
                </p>
                <div className="grid grid-cols-2 gap-4 pt-6">
                  <div className="text-center p-4 bg-yellow-50 rounded-xl">
                    <div className="text-3xl font-bold text-gray-900">15+</div>
                    <div className="text-sm text-gray-600 mt-1">Yıllık Deneyim</div>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-xl">
                    <div className="text-3xl font-bold text-gray-900">7/24</div>
                    <div className="text-sm text-gray-600 mt-1">Kesintisiz Hizmet</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Müşteri Yorumları</h2>
            <p className="text-xl text-gray-600">Müşterilerimizin memnuniyeti bizim önceliğimiz</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 hover:border-yellow-400 transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.comment}"</p>
                  <p className="font-semibold text-gray-900">- {testimonial.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">İletişim</h2>
            <p className="text-xl text-gray-600">Bize ulaşın, size yardımcı olalım</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-2xl">İletişim Bilgileri</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Phone className="w-6 h-6 text-yellow-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Telefon</p>
                      <a href={`tel:${companyInfo.phone}`} className="text-gray-700 hover:text-yellow-600 transition-colors">
                        {companyInfo.phone}
                      </a>
                      <br />
                      <a href={`tel:${companyInfo.mobile}`} className="text-gray-700 hover:text-yellow-600 transition-colors">
                        {companyInfo.mobile}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-6 h-6 text-yellow-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Adres</p>
                      <p className="text-gray-700">{companyInfo.address}</p>
                      <p className="text-gray-700">{companyInfo.city}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-6 h-6 text-yellow-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Çalışma Saatleri</p>
                      <p className="text-gray-700">7/24 Açık</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Google Maps */}
              <Card className="border-2 overflow-hidden">
                <CardContent className="p-0">
                  <iframe
                    title="Gelibolu Hastane Taksi Durağı Konum"
                    src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3037.5!2d${companyInfo.coordinates.lng}!3d${companyInfo.coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDI0JzQxLjAiTiAyNsKwNDAnMTQuOSJF!5e0!3m2!1str!2str!4v1234567890!5m2!1str!2str`}
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold text-yellow-400 mb-4">{companyInfo.name}</h3>
              <p className="text-gray-400">
                Gelibolu'da güvenilir ve profesyonel taksi hizmeti
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Hızlı Bağlantılar</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="text-gray-400 hover:text-yellow-400 transition-colors">Hizmetlerimiz</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-yellow-400 transition-colors">Hakkımızda</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-yellow-400 transition-colors">İletişim</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">İletişim</h4>
              <ul className="space-y-2 text-gray-400">
                <li>{companyInfo.phone}</li>
                <li>{companyInfo.mobile}</li>
                <li>{companyInfo.address}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2024 {companyInfo.name}. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>

      {/* Floating Call Button */}
      <div className="fixed bottom-8 right-8 z-50">
        <Button
          onClick={handleCallNow}
          size="lg"
          className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-full w-16 h-16 shadow-2xl transform hover:scale-110 transition-all duration-300 animate-pulse"
        >
          <Phone className="w-8 h-8" />
        </Button>
      </div>
    </div>
  );
};

export default Home;
